import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Select,
  MenuItem,
  Badge,
  Avatar,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CloseIcon from '@mui/icons-material/Close';

import config from '../config';

// Brand color for the customer-facing menu (unchanged theme).
const BRAND = '#16243f';

// Order + labels for the QR menu food-type filter dropdown (admin-controlled).
 const FOOD_TYPE_ORDER = [
  ['Veg', 'Veg only'],
  ['NonVeg', 'Non-Veg only'],
  ['Egg', 'Egg only'],
  ['Vegan', 'Vegan only'],
  ['Jain', 'Jain only'],
];

const MEDIA_BASE = (config.API_BASE_URL || '').replace(/\/api$/, '');
const resolveImg = (p) => {
  if (!p) return '';
  if (p.startsWith('data:') || p.startsWith('http')) return p;
  return `${MEDIA_BASE}${p.startsWith('/') ? '' : '/'}${p}`;
};

// Veg / Non-Veg / Egg marker.
function FoodMark({ type }) {
  const color =
    type === 'NonVeg'
      ? '#c0392b'
      : type === 'Egg'
        ? '#e67e22'
        : type === 'Jain'
          ? '#ca8a04'
          : type === 'Vegan'
            ? '#0e9488'
            : '#0a8a3c';
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        border: `1.5px solid ${color}`,
        borderRadius: '3px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          bgcolor: color,
          borderRadius: type === 'NonVeg' ? 0 : '50%',
          clipPath:
            type === 'NonVeg' ? 'polygon(50% 0, 100% 100%, 0 100%)' : 'none',
        }}
      />
    </Box>
  );
}

// QR params are packed into an opaque ?d=<base64> token so guests cannot read
// the client id / table name. Legacy plain ?client=&tablename= still works.
const encodeQrParams = (obj) =>
  btoa(encodeURIComponent(new URLSearchParams(obj).toString()));
const decodeQrParams = (raw) => {
  const d = raw.get('d');
  if (!d) return raw;
  try {
    const inner = new URLSearchParams(decodeURIComponent(atob(d)));
    raw.forEach((v, k) => {
      if (k !== 'd') inner.set(k, v);
    });
    return inner;
  } catch {
    return raw;
  }
};

export default function RoomServiceOrder() {
  const [rawParams] = useSearchParams();
  const searchParams = useMemo(() => decodeQrParams(rawParams), [rawParams]);

  const clientFromUrl = searchParams.get('client');
  const tableNameFromUrl = searchParams.get('tablename');

  const [company, setCompany] = useState(null);
  const [companyReady, setCompanyReady] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState(
    tableNameFromUrl || '',
  );

  const [view, setView] = useState('landing'); // 'landing' | 'menu'

  const [accessMode, setAccessMode] = useState('ScanOrder');
  const [qrEnabled, setQrEnabled] = useState(true);
  // Area-level online payment (CC Avenue / AllUPI). When true the checkout
  // routes through the gateway instead of a straight pay-at-counter order.
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  // Category ids hidden for THIS table (admin-controlled per QR). Empty = all.
  const [hiddenCatIds, setHiddenCatIds] = useState(() => new Set());
  const canOrder = accessMode === 'ScanOrder' && qrEnabled;

  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [variantMap, setVariantMap] = useState({});
  const [addonMap, setAddonMap] = useState({}); // #16: itemId -> [{id,name,price}]
  const [itemSearch, setItemSearch] = useState('');
  const [foodFilter, setFoodFilter] = useState('all'); // 'all' | a food-type string
  const [qrFoodTypes, setQrFoodTypes] = useState({ Veg: true, NonVeg: true, Egg: true, Vegan: true, Jain: true });
  // #2: show item descriptions on the QR menu? (admin display setting; default on)
  const [descOnQr, setDescOnQr] = useState(true);

  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [customize, setCustomize] = useState(null); // #16: { item, variant, addons }
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchValue, setSwitchValue] = useState('');
  const [notes, setNotes] = useState('');
  const [payOpen, setPayOpen] = useState(false); // gateway-choice dialog

  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ── Category scroll-spy (left rail) ──
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});
  const [activeCat, setActiveCat] = useState(null);

  const Companycurrency = company?.currency ?? '₹';

  // ==================== LOAD COMPANY ====================
  useEffect(() => {
    const loadCompany = async () => {
      const client = clientFromUrl || localStorage.getItem('client') || 'Demo';
      try {
        setLoading(true);
        const res = await fetch(
          `${config.API_BASE_URL}/account/initQrCode?client=${client}`,
        );
        const companyData = await res.json();
        if (!companyData?.id) throw new Error('Invalid company data');

        sessionStorage.setItem('companyDetails', JSON.stringify(companyData));
        sessionStorage.setItem('companyId', companyData.id);
        setCompany(companyData);
        setCompanyReady(true);
        setSelectedTableName(tableNameFromUrl || '');
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: 'Failed to load company data',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    loadCompany();
  }, [clientFromUrl, tableNameFromUrl]);

  // Returning from the payment gateway (?paystatus=success|failed). Show the
  // result, drop the guest straight onto the menu, and clean the URL.
  useEffect(() => {
    const ps = searchParams.get('paystatus');
    if (!ps) return;
    setView('menu');
    setCart({});
    setSnackbar({
      open: true,
      message:
        ps === 'success'
          ? 'Payment successful — your order is confirmed!'
          : 'Payment failed or cancelled. Please try again.',
      severity: ps === 'success' ? 'success' : 'error',
    });
    const c = searchParams.get('client') || '';
    const t = searchParams.get('tablename') || '';
    window.history.replaceState(
      {},
      '',
        `${window.location.pathname}?d=${encodeURIComponent(
          encodeQrParams({ client: c, tablename: t }),
        )}`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== LOAD TABLE ACCESS MODE + HIDDEN CATEGORIES ========
  const loadAccessMode = useCallback(async (companyId, tableName) => {
    if (!companyId || !tableName) {
      setAccessMode('ScanOrder');
      setQrEnabled(true);
      setPaymentEnabled(false);
      setHiddenCatIds(new Set());
      return;
    }
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/qrmenu/table-info/${companyId}/${encodeURIComponent(
          tableName,
        )}`,
      );
      const info = await res.json();
      setAccessMode(info?.accessMode || 'ScanOrder');
      setQrEnabled(info?.isQrEnabled !== false);
      setPaymentEnabled(info?.paymentEnabled === true);
      const hidden = info?.hiddenCategoryIds || info?.HiddenCategoryIds || [];
      setHiddenCatIds(new Set(hidden));
    } catch {
      setAccessMode('ScanOrder');
      setQrEnabled(true);
      setPaymentEnabled(false);
      setHiddenCatIds(new Set());
    }
  }, []);

  useEffect(() => {
    if (company?.id) loadAccessMode(company.id, selectedTableName);
  }, [company, selectedTableName, loadAccessMode]);

  // ==================== FETCH CATEGORIES + ITEMS + VARIANTS ====================
  useEffect(() => {
    if (!company?.id) return;

    fetch(`${config.API_BASE_URL}/account/GetAllCategory/${company.id}`)
      .then((r) => r.json())
      .then((d) => setCategories(d || []))
      .catch(() => setCategories([]));

    // #2: whether the QR menu should show item descriptions (admin setting).
    fetch(`${config.API_BASE_URL}/qrmenu/desc-display/${company.id}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.json) {
          try {
            const cfg = JSON.parse(d.json);
            setDescOnQr(cfg.qrMenu !== false);
            if (cfg.qrFoodTypes && typeof cfg.qrFoodTypes === 'object')
              setQrFoodTypes((p) => ({ ...p, ...cfg.qrFoodTypes }));
          } catch {
            /* keep default on */
          }
        }
      })
      .catch(() => {});

    setItemsLoading(true);
    fetch(`${config.API_BASE_URL}/Home/GetallItemsAsync/${company.id}`)
      .then((r) => r.json())
      .then((d) => setAllItems(d || []))
      .catch(() => setAllItems([]))
      .finally(() => setItemsLoading(false));

    fetch(`${config.API_BASE_URL}/Home/GetSubcategories/${company.id}`)
      .then((r) => r.json())
      .then((list) => {
        const map = {};
        (list || []).forEach((v) => {
          if (v.activeStatus === false) return;
          const pid = v.parentItemId ?? v.ParentItemId;
          if (!pid) return;
          (map[pid] = map[pid] || []).push({
            id: v.id ?? v.Id,
            name: v.subcategoryName ?? v.SubcategoryName,
            price: v.sellingPrice ?? v.SellingPrice ?? 0,
          });
        });
        setVariantMap(map);
      })
      .catch(() => setVariantMap({}));

    // #16: item -> add-ons map (Extra Cheese, Extra Corn ...).
    fetch(`${config.API_BASE_URL}/qrmenu/item-addons/${company.id}`)
      .then((r) => r.json())
      .then((list) => {
        const map = {};
        (list || []).forEach((a) => {
          if (!a.itemId) return;
          (map[a.itemId] = map[a.itemId] || []).push({
            id: a.id,
            name: a.name,
            price: Number(a.price) || 0,
          });
        });
        setAddonMap(map);
      })
      .catch(() => setAddonMap({}));
  }, [company]);

  // ==================== DERIVED ====================
  // Categories the guest can see (hidden ones removed per QR settings).
  const visibleCategories = useMemo(
    () => categories.filter((c) => !hiddenCatIds.has(c.id)),
    [categories, hiddenCatIds],
  );

  const visibleItems = useMemo(() => {
    const q = itemSearch.toLowerCase().trim();
    return allItems.filter((item) => {
      if (hiddenCatIds.has(item.cateid)) return false;
      const ft = item.foodType || 'Veg';
      // Food-type filter: 'all' or an exact type (Veg/NonVeg/Egg/Vegan/Jain).
      if (foodFilter !== 'all' && ft !== foodFilter) return false;
      if (q) return item.name?.toLowerCase().includes(q);
      return true;
    });
  }, [allItems, itemSearch, foodFilter, hiddenCatIds]);

  const groupedItems = useMemo(() => {
    const byId = {};
    visibleCategories.forEach((c) => (byId[c.id] = c.category_name));
    const groups = [];
    const index = {};
    visibleItems.forEach((item) => {
      const key = item.cateid;
      if (byId[key] == null) return; // belongs to a hidden category
      if (index[key] == null) {
        index[key] = groups.length;
        groups.push({ id: key, name: byId[key] || 'Menu', items: [] });
      }
      groups[index[key]].items.push(item);
    });
    return groups;
  }, [visibleItems, visibleCategories]);

  useEffect(() => {
    if (groupedItems.length && activeCat == null)
      setActiveCat(groupedItems[0].id);
  }, [groupedItems, activeCat]);

  const cartArray = useMemo(() => Object.values(cart), [cart]);
  const cartCount = cartArray.reduce((s, c) => s + c.qty, 0);
  // #16: unit price = base/variant + selected add-ons.
  const lineUnit = (item, variant, addons) =>
    (variant ? variant.price : item.price || 0) +
    (addons || []).reduce((s, a) => s + (a.price || 0), 0);
  const cartTotal = cartArray.reduce(
    (s, c) => s + c.qty * lineUnit(c.item, c.variant, c.addons),
    0,
  );

  const keyFor = (item, variant, addons) =>
    `${item.id}_${variant ? variant.id : 0}_${(addons || [])
      .map((a) => a.id)
      .sort((x, y) => x - y)
      .join('-')}`;
  const qtyInCart = (item) =>
    cartArray.filter((c) => c.item.id === item.id).reduce((s, c) => s + c.qty, 0);

  // ── scroll-spy handlers ──
  const scrollToCat = (id) => {
    const el = sectionRefs.current[id];
    const cont = scrollRef.current;
    if (el && cont)
      cont.scrollTo({ top: el.offsetTop - cont.offsetTop - 8, behavior: 'smooth' });
  };
  const handleSpy = () => {
    const cont = scrollRef.current;
    if (!cont) return;
    const pos = cont.scrollTop + 16;
    let cur = groupedItems[0]?.id;
    for (const g of groupedItems) {
      const el = sectionRefs.current[g.id];
      if (el && el.offsetTop - cont.offsetTop <= pos) cur = g.id;
    }
    if (cur != null) setActiveCat(cur);
  };

  // ==================== CART OPS ====================
  const addToCart = (item, variant = null, addons = []) => {
    const k = keyFor(item, variant, addons);
    setCart((prev) => ({
      ...prev,
      [k]: { item, variant, addons, qty: (prev[k]?.qty || 0) + 1 },
    }));
  };
  const decFromCart = (k) => {
    setCart((prev) => {
      const next = { ...prev };
      if (!next[k]) return next;
      const q = next[k].qty - 1;
      if (q <= 0) delete next[k];
      else next[k] = { ...next[k], qty: q };
      return next;
    });
  };
  const onAdd = (item) => {
    if (!canOrder) return;
    const hasV = (variantMap[item.id] || []).length > 0;
    const hasA = (addonMap[item.id] || []).length > 0;
    if (hasV || hasA) setCustomize({ item, variant: null, addons: [] });
    else addToCart(item, null, []);
  };

  // ==================== PLACE ORDER (reuses SaveOrder + KOT) ====
  const getItemsByDepartment = () => {
    const grouped = {};
    cartArray.forEach(({ item, variant, addons, qty }) => {
      const dept = item.department || 'General';
      (grouped[dept] = grouped[dept] || []).push({ item, variant, addons, qty });
    });
    return grouped;
  };

  const sendToPrinterAPI = async () => {
    const grouped = getItemsByDepartment();
    const machineId = window.location.hostname;
    const dateTime = new Date().toLocaleString();
    for (const [dept, deptItems] of Object.entries(grouped)) {
      let kotNo = localStorage.getItem('kotNo') || 1;
      localStorage.setItem('kotNo', parseInt(kotNo) + 1);
      const itemsText = deptItems
        .map(({ item, variant, addons, qty }) => {
          const q = String(qty).padEnd(3, ' ');
          const addonTxt =
            (addons || []).length > 0
              ? ' +' + addons.map((a) => a.name).join('+')
              : '';
          const nm = `${item.name}${variant ? ' (' + variant.name + ')' : ''}${addonTxt}`
            .substring(0, 24)
            .padEnd(24, ' ');
          return `${q}| ${nm}`;
        })
        .join('\n');
      const divider = '--------------------------------\n';
      const data =
        '\x1B\x40\x1B\x61\x01' +
        (company?.name || 'HOTEL') +
        '\n' +
        divider +
        '\x1B\x45\x01KITCHEN ORDER TICKET\n\x1B\x45\x00' +
        divider +
        `KOT  : ${kotNo}\nDEPT : ${dept}\nTABLE: ${selectedTableName}\nTIME : ${dateTime}\n` +
        divider +
        'QTY | ITEM\n' +
        divider +
        itemsText +
        '\n' +
        divider +
        `NOTES:\n${notes || '-'}\n` +
        divider +
        '\x1B\x61\x01***** THANK YOU *****\n\n\n\n\n\n\n\x1D\x56\x00';
      await fetch(`${config.API_BASE_URL}/Service/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'KOT',
          content: data,
          machineId,
          companyid: company?.id,
          printerName: dept,
        }),
      });
    }
  };

  const buildPayload = () => ({
    BookingId: 0,
    RoomNo: 0,
    Tableno: selectedTableName,
    Items: cartArray.map(({ item, variant, addons, qty }) => ({
      ItemId: item.id,
      SubcategoryId: variant ? variant.id : 0,
      Qty: qty,
      // #16: unit price includes selected add-ons so the bill reflects them.
      Price:
        (addons || []).length > 0
          ? lineUnit(item, variant, addons)
          : variant
            ? variant.price
            : null,
    })),
    Notes: notes.trim() || '',
    CompanyId: company?.id,
    userid: 1,
  });

  // Save the order and return its new id (0 on failure).
  const saveOrder = async () => {
    const res = await fetch(`${config.API_BASE_URL}/account/SaveOrder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload()),
    });
    const d = await res.json().catch(() => ({}));
    return d?.orderId || d?.id || 0;
  };

  // Pay-at-counter flow (unchanged): save + print KOT + done.
  const placeOrder = async () => {
    if (!cartArray.length) return;
    setSubmitting(true);
    try {
      const orderId = await saveOrder();
      if (!orderId) throw new Error();
      await sendToPrinterAPI();
      setSnackbar({
        open: true,
        message: 'Order placed successfully!',
        severity: 'success',
      });
      setCart({});
      setNotes('');
      setCartOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Failed to place order', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!cartArray.length) return;
    if (paymentEnabled) {
      setCartOpen(false);
      setPayOpen(true); // choose gateway
    } else {
      placeOrder();
    }
  };

  // Auto-submit a hidden form to CC Avenue (same helper as the subscription
  // payment). Navigates the browser to the gateway.
  const submitToCCAvenue = (gatewayUrl, encRequest, accessCode) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = gatewayUrl;
    const add = (n, v) => {
      const i = document.createElement('input');
      i.type = 'hidden';
      i.name = n;
      i.value = v;
      form.appendChild(i);
    };
    add('encRequest', encRequest);
    add('access_code', accessCode);
    document.body.appendChild(form);
    form.submit();
  };

  // Online-payment flow: save the order (so the kitchen has it), print KOT,
  // then hand off to the chosen gateway. On success the backend marks the order
  // paid and redirects back to this page with ?paystatus=success.
  const payAndPlace = async (gateway) => {
    setPayOpen(false);
    setSubmitting(true);
    try {
      const orderId = await saveOrder();
      if (!orderId) throw new Error('Could not save the order.');
      await sendToPrinterAPI();

      const client =
        clientFromUrl || localStorage.getItem('client') || 'Demo';
      const returnUrl =
        `${window.location.origin}${window.location.pathname}` +
        `?client=${encodeURIComponent(client)}&tablename=${encodeURIComponent(
          selectedTableName,
        )}`;
      const body = JSON.stringify({
        CompanyId: company?.id,
        OrderId: orderId,
        // Charge only THIS guest's cart — orders merge into one running table
        // order, so the backend must not charge the whole table.
        Amount: Number(cartTotal.toFixed(2)),
        ReturnUrl: returnUrl,
      });

      if (gateway === 'ccavenue') {
        const res = await fetch(
          `${config.API_BASE_URL}/orderpay/ccavenue/initiate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          },
        );
        const d = await res.json();
        if (!d?.success) throw new Error(d?.message || 'Payment could not start.');
        submitToCCAvenue(d.gatewayUrl, d.encRequest, d.accessCode);
      } else {
        const res = await fetch(
          `${config.API_BASE_URL}/orderpay/allupi/initiate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          },
        );
        const d = await res.json();
        if (!d?.success || !d?.paymentUrl)
          throw new Error(d?.message || 'Payment could not start.');
        window.location.href = d.paymentUrl;
      }
      // On success the browser navigates away to the gateway; keep the spinner.
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Payment failed to start',
        severity: 'error',
      });
      setSubmitting(false);
    }
  };

  // ==================== LOADING GATE ====================
  if (!companyReady) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
        }}>
        <CircularProgress sx={{ color: BRAND }} />
      </Box>
    );
  }

  const branchLabel = company?.city || company?.state || '';
  const fullName = branchLabel
    ? `${company?.name} - ${branchLabel}`
    : company?.name;
  // Restaurant brand: use the company's own logo when uploaded, else fall back
  // to the "dine" restaurant logo (instead of goerp/no logo).
  const logoSrc = resolveImg(company?.logo) || '/restaurant-logo.png';

  // ==================== LANDING (reference) ====================
  if (view === 'landing') {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 6,
          textAlign: 'center',
        }}>
        {logoSrc ? (
          <Box
            component='img'
            src={logoSrc}
            alt={company?.name}
            sx={{ maxWidth: 200, maxHeight: 150, objectFit: 'contain', mb: 1 }}
          />
        ) : (
          <RestaurantMenuIcon sx={{ fontSize: 72, color: BRAND, mb: 1 }} />
        )}

        <Typography variant='h5' fontWeight={800} sx={{ color: '#1f2937' }}>
          {fullName}
        </Typography>

        {company?.openingHours && (
          <Typography sx={{ color: '#6b7280', mt: 0.5 }}>
            {company.openingHours}
          </Typography>
        )}

        <Box sx={{ mt: 5 }}>
          <Typography variant='h4' fontWeight={800} sx={{ color: '#111827' }}>
            {selectedTableName ? `Table ${selectedTableName}` : 'Welcome'}
          </Typography>
          {selectedTableName && (
            <Button
              onClick={() => {
                setSwitchValue(selectedTableName);
                setSwitchOpen(true);
              }}
              sx={{ color: BRAND, fontWeight: 700, mt: 0.5 }}>
              SWITCH
            </Button>
          )}
        </Box>

        <Button
          fullWidth
          variant='contained'
          onClick={() => setView('menu')}
          sx={{
            mt: 6,
            maxWidth: 320,
            py: 1.5,
            borderRadius: 2,
            bgcolor: BRAND,
            fontWeight: 700,
            fontSize: '1rem',
            '&:hover': { bgcolor: '#0f1a30' },
          }}>
          VIEW MENU
        </Button>

        {!qrEnabled && (
          <Typography sx={{ color: '#c0392b', mt: 2, fontSize: 13 }}>
            Ordering is currently disabled for this table.
          </Typography>
        )}

        <SwitchTableDialog
          open={switchOpen}
          value={switchValue}
          onChange={setSwitchValue}
          onClose={() => setSwitchOpen(false)}
          onSave={() => {
            setSelectedTableName(switchValue.trim());
            setSwitchOpen(false);
          }}
        />
      </Box>
    );
  }

  // ==================== MENU ====================
  return (
    <Box
      sx={{
        height: '100dvh',
        overflow: 'hidden',
        bgcolor: '#f3f4f6',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* Header: logo + name + branch + table */}
      <Box
        sx={{
          bgcolor: '#fff',
          px: 2,
          pt: 1.5,
          pb: 1,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          flexShrink: 0,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            {logoSrc && (
              <Box
                component='img'
                src={logoSrc}
                alt=''
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  objectFit: 'contain',
                  flexShrink: 0,
                  border: '1px solid #f1f1f1',
                }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={800} noWrap sx={{ color: '#111827', lineHeight: 1.2 }}>
                {company?.name}
              </Typography>
              <Typography sx={{ color: '#6b7280', fontSize: 12.5 }} noWrap>
                {branchLabel ? `${branchLabel} · ` : ''}Table{' '}
                {selectedTableName || '-'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setView('landing')} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Veg toggle + search */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Select
            size='small'
            value={foodFilter}
            onChange={(e) => setFoodFilter(e.target.value)}
            sx={{
              flexShrink: 0,
              borderRadius: 5,
              bgcolor: '#fff',
              minWidth: 70,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                py: 0.6,
                pl: 1,
              },
            }}
            renderValue={(val) =>
              val === 'all' ? (
                <Box sx={{ display: 'flex', gap: 0.4 }}>
                  {FOOD_TYPE_ORDER.filter(([k]) => qrFoodTypes[k])
                    .slice(0, 2)
                    .map(([k]) => (
                      <FoodMark key={k} type={k} />
                    ))}
                </Box>
              ) : (
                <FoodMark type={val} />
              )
            }>
            <MenuItem value='all'>ALL</MenuItem>
            {FOOD_TYPE_ORDER.filter(([k]) => qrFoodTypes[k]).map(([k, label]) => (
              <MenuItem key={k} value={k}>
                <FoodMark type={k} />
                <Box component='span' sx={{ ml: 0.75 }}>
                  {label}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <TextField
            fullWidth
            size='small'
            placeholder='Search in Menu'
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: '#9ca3af' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 5, bgcolor: '#fff' },
            }}
          />
        </Box>
      </Box>

      {/* View-only banner */}
      {!canOrder && (
        <Box
          sx={{
            bgcolor: '#fff7ed',
            color: '#9a3412',
            px: 2,
            py: 1,
            fontSize: 13,
            textAlign: 'center',
            flexShrink: 0,
          }}>
          {qrEnabled
            ? 'Menu only — please call a waiter to order.'
            : 'Ordering is disabled for this table.'}
        </Box>
      )}

      {/* Body: left category rail + right items */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Left vertical category navigation */}
        <Box
          sx={{
            width: { xs: 80, sm: 104 },
            flexShrink: 0,
            bgcolor: '#fff',
            borderRight: '1px solid #eef0f3',
            overflowY: 'auto',
            py: 1,
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
          {visibleCategories.map((cat) => {
            const active = activeCat === cat.id;
            const img = resolveImg(cat.cateimg);
            return (
              <Box
                key={cat.id}
                onClick={() => scrollToCat(cat.id)}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.5,
                  py: 1.25,
                  cursor: 'pointer',
                  transition: 'background .2s',
                  bgcolor: active ? 'rgba(22,36,63,0.06)' : 'transparent',
                  '&:before': active
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        bottom: 8,
                        width: 3,
                        borderRadius: 3,
                        bgcolor: BRAND,
                      }
                    : {},
                }}>
                <Avatar
                  src={img || undefined}
                  variant='rounded'
                  sx={{
                    width: 46,
                    height: 46,
                    bgcolor: active ? BRAND : '#eef1f6',
                    color: active ? '#fff' : '#9aa3b2',
                    transition: 'all .2s',
                    transform: active ? 'scale(1.06)' : 'scale(1)',
                  }}>
                  {!img && <RestaurantMenuIcon fontSize='small' />}
                </Avatar>
                <Typography
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.15,
                    textAlign: 'center',
                    fontWeight: active ? 800 : 600,
                    color: active ? BRAND : '#4b5563',
                    px: 0.25,
                  }}>
                  {cat.category_name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Right scrollable items */}
        <Box
          ref={scrollRef}
          onScroll={handleSpy}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            px: 1.75,
            py: 2,
            pb: 12,
          }}>
          {itemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: BRAND }} />
            </Box>
          ) : groupedItems.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#9ca3af', py: 8 }}>
              No items found.
            </Typography>
          ) : (
            groupedItems.map((group) => (
              <Box
                key={group.id}
                ref={(el) => (sectionRefs.current[group.id] = el)}
                sx={{ mb: 3 }}>
                {/* Prominent category heading */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                  <Typography
                    sx={{
                      fontSize: '1.35rem',
                      fontWeight: 800,
                      letterSpacing: 0.3,
                      color: '#111827',
                    }}>
                    {group.name}
                  </Typography>
                  <Box sx={{ flex: 1, height: '2px', bgcolor: '#e5e7eb', borderRadius: 2 }} />
                </Box>

                {group.items.map((item) => {
                  const inCart = qtyInCart(item);
                  const customizable =
                    (variantMap[item.id] || []).length > 0 ||
                    (addonMap[item.id] || []).length > 0;
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        bgcolor: '#fff',
                        borderRadius: 3,
                        p: 1.5,
                        mb: 1.5,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <FoodMark type={item.foodType || 'Veg'} />
                        <Typography fontWeight={700} sx={{ color: '#1f2937', mt: 0.5 }}>
                          {item.name}
                        </Typography>
                        {descOnQr && item.description && (
                          <Typography
                            sx={{
                              color: '#6b7280',
                              fontSize: 12.5,
                              mt: 0.25,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                            {item.description}
                          </Typography>
                        )}
                        <Typography sx={{ color: '#111827', fontWeight: 600, mt: 0.5 }}>
                          {Companycurrency}
                          {item.price}
                        </Typography>
                      </Box>

                      <Box sx={{ width: 110, flexShrink: 0, position: 'relative' }}>
                        {item.image ? (
                          <Box
                            component='img'
                            src={resolveImg(item.image)}
                            alt={item.name}
                            sx={{
                              width: '100%',
                              height: 90,
                              objectFit: 'cover',
                              borderRadius: 2,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 90,
                              borderRadius: 2,
                              bgcolor: '#f3f4f6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            <RestaurantMenuIcon sx={{ color: '#d1d5db' }} />
                          </Box>
                        )}

                        {canOrder &&
                          (inCart > 0 ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: customizable ? 12 : -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: '#fff',
                                border: `1px solid ${BRAND}`,
                                borderRadius: 2,
                                boxShadow: 2,
                              }}>
                              <IconButton
                                size='small'
                                onClick={() => {
                                  const line = cartArray
                                    .filter((c) => c.item.id === item.id)
                                    .slice(-1)[0];
                                  if (line)
                                    decFromCart(
                                      keyFor(line.item, line.variant, line.addons),
                                    );
                                }}>
                                <RemoveIcon fontSize='small' sx={{ color: BRAND }} />
                              </IconButton>
                              <Typography fontWeight={700} sx={{ color: BRAND, px: 0.5 }}>
                                {inCart}
                              </Typography>
                              <IconButton size='small' onClick={() => onAdd(item)}>
                                <AddIcon fontSize='small' sx={{ color: BRAND }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              onClick={() => onAdd(item)}
                              sx={{
                                position: 'absolute',
                                bottom: customizable ? 12 : -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bgcolor: '#fff',
                                color: BRAND,
                                fontWeight: 800,
                                border: `1px solid ${BRAND}`,
                                borderRadius: 2,
                                px: 3,
                                boxShadow: 2,
                                '&:hover': { bgcolor: '#fff' },
                              }}>
                              ADD
                            </Button>
                          ))}

                        {customizable && (
                          <Typography
                            sx={{
                              position: 'absolute',
                              bottom: -8,
                              left: 0,
                              right: 0,
                              textAlign: 'center',
                              fontSize: 10,
                              color: '#9ca3af',
                            }}>
                            customizable
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Cart bar */}
      {canOrder && cartCount > 0 && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 20 }}>
          <Button
            fullWidth
            variant='contained'
            onClick={() => setCartOpen(true)}
            sx={{
              bgcolor: BRAND,
              py: 1.4,
              borderRadius: 3,
              fontWeight: 700,
              justifyContent: 'space-between',
              '&:hover': { bgcolor: '#0f1a30' },
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge badgeContent={cartCount} color='error'>
                <ShoppingCartIcon />
              </Badge>
              View Cart
            </Box>
            <span>
              {Companycurrency}
              {cartTotal.toFixed(0)}
            </span>
          </Button>
        </Box>
      )}

      {/* Cart drawer */}
      <Drawer
        anchor='bottom'
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh' },
        }}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}>
            <Typography variant='h6' fontWeight={800}>
              Your Order · Table {selectedTableName}
            </Typography>
            <IconButton onClick={() => setCartOpen(false)} size='small'>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />

          {cartArray.map((c) => {
            const k = keyFor(c.item, c.variant, c.addons);
            const unit = lineUnit(c.item, c.variant, c.addons);
            return (
              <Box
                key={k}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FoodMark type={c.item.foodType || 'Veg'} />
                    <Typography fontWeight={600} noWrap>
                      {c.item.name}
                      {c.variant ? ` (${c.variant.name})` : ''}
                    </Typography>
                  </Box>
                  {(c.addons || []).length > 0 && (
                    <Typography sx={{ color: '#9ca3af', fontSize: 11 }} noWrap>
                      + {c.addons.map((a) => a.name).join(', ')}
                    </Typography>
                  )}
                  <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
                    {Companycurrency}
                    {unit}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `1px solid ${BRAND}`,
                    borderRadius: 2,
                  }}>
                  <IconButton size='small' onClick={() => decFromCart(k)}>
                    <RemoveIcon fontSize='small' sx={{ color: BRAND }} />
                  </IconButton>
                  <Typography fontWeight={700} sx={{ px: 0.5, color: BRAND }}>
                    {c.qty}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => addToCart(c.item, c.variant, c.addons)}>
                    <AddIcon fontSize='small' sx={{ color: BRAND }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}

          <TextField
            fullWidth
            multiline
            rows={2}
            size='small'
            placeholder='Special instructions / notes'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 1.5 }}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}>
            <Typography fontWeight={800}>Total</Typography>
            <Typography fontWeight={800} sx={{ color: BRAND }}>
              {Companycurrency}
              {cartTotal.toFixed(2)}
            </Typography>
          </Box>

          <Button
            fullWidth
            variant='contained'
            disabled={submitting || !cartArray.length}
            onClick={handleSubmit}
            sx={{
              mt: 2,
              py: 1.3,
              borderRadius: 2,
              bgcolor: BRAND,
              fontWeight: 700,
              '&:hover': { bgcolor: '#0f1a30' },
            }}>
            {submitting ? (
              <CircularProgress size={22} color='inherit' />
            ) : (
              'Place Order'
            )}
          </Button>
        </Box>
      </Drawer>

      {/* #16: Customize — variant (radio) + add-ons (checkboxes) */}
      <Dialog
        open={!!customize}
        onClose={() => setCustomize(null)}
        fullWidth
        maxWidth='xs'>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {customize?.item?.name}
          <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
            Customize your item
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Variants */}
          {customize && (variantMap[customize.item.id] || []).length > 0 && (
            <>
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                Choose an option
              </Typography>
              {(variantMap[customize.item.id] || []).map((v) => {
                const sel = customize.variant?.id === v.id;
                return (
                  <Box
                    key={v.id}
                    onClick={() => setCustomize((c) => ({ ...c, variant: v }))}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      px: 1,
                      mb: 0.5,
                      borderRadius: 1.5,
                      border: `1px solid ${sel ? BRAND : '#eee'}`,
                      bgcolor: sel ? 'rgba(22,36,63,0.05)' : 'transparent',
                      cursor: 'pointer',
                    }}>
                    <Typography fontWeight={600}>{v.name}</Typography>
                    <Typography fontWeight={700} sx={{ color: BRAND }}>
                      {Companycurrency}
                      {v.price}
                    </Typography>
                  </Box>
                );
              })}
            </>
          )}

          {/* Add-ons */}
          {customize && (addonMap[customize.item.id] || []).length > 0 && (
            <>
              <Typography fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
                Add-ons
              </Typography>
              {(addonMap[customize.item.id] || []).map((a) => {
                const checked = customize.addons.some((x) => x.id === a.id);
                return (
                  <Box
                    key={a.id}
                    onClick={() =>
                      setCustomize((c) => ({
                        ...c,
                        addons: checked
                          ? c.addons.filter((x) => x.id !== a.id)
                          : [...c.addons, a],
                      }))
                    }
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      px: 1,
                      cursor: 'pointer',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: 0.5,
                          border: `2px solid ${checked ? BRAND : '#cbd5e1'}`,
                          bgcolor: checked ? BRAND : '#fff',
                          color: '#fff',
                          fontSize: 13,
                          textAlign: 'center',
                          lineHeight: '15px',
                        }}>
                        {checked ? '✓' : ''}
                      </Box>
                      <Typography fontWeight={600}>{a.name}</Typography>
                    </Box>
                    <Typography fontWeight={700} sx={{ color: BRAND }}>
                      +{Companycurrency}
                      {a.price}
                    </Typography>
                  </Box>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomize(null)}>Cancel</Button>
          <Button
            variant='contained'
            disabled={
              customize &&
              (variantMap[customize.item.id] || []).length > 0 &&
              !customize.variant
            }
            onClick={() => {
              addToCart(customize.item, customize.variant, customize.addons);
              setCustomize(null);
            }}
            sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#0f1a30' } }}>
            Add{' '}
            {Companycurrency}
            {customize
              ? lineUnit(customize.item, customize.variant, customize.addons)
              : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <SwitchTableDialog
        open={switchOpen}
        value={switchValue}
        onChange={setSwitchValue}
        onClose={() => setSwitchOpen(false)}
        onSave={() => {
          setSelectedTableName(switchValue.trim());
          setSwitchOpen(false);
        }}
      />

      {/* Payment gateway choice (same gateways as the subscription upgrade) */}
      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        fullWidth
        maxWidth='xs'>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Pay {Companycurrency}
          {cartTotal.toFixed(2)}
          <Typography sx={{ color: '#6b7280', fontSize: 13 }}>
            Table {selectedTableName} · choose a payment method
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Button
            fullWidth
            variant='contained'
            onClick={() => payAndPlace('ccavenue')}
            sx={{
              mb: 1.5,
              py: 1.3,
              borderRadius: 2,
              bgcolor: BRAND,
              fontWeight: 700,
              '&:hover': { bgcolor: '#0f1a30' },
            }}>
            Pay with CC Avenue (Card / UPI / Netbanking)
          </Button>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => payAndPlace('allupi')}
            sx={{
              py: 1.3,
              borderRadius: 2,
              fontWeight: 700,
              color: BRAND,
              borderColor: BRAND,
            }}>
            Pay with AllUPI (UPI QR)
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant='filled'>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function SwitchTableDialog({ open, value, onChange, onClose, onSave }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle sx={{ fontWeight: 800 }}>Switch Table</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label='Table number'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={onSave} disabled={!value.trim()}>
          Switch
        </Button>
      </DialogActions>
    </Dialog>
  );
}
